import './Users.css';
import React, { useState } from 'react';
import { Container } from '../../components/Container/Container';
import { Card } from '../../components/Card/Card';
import { Pagination } from '../../components/Pagination/Pagination';
import { SortableList } from '../../components/SortableList/SortableList';
import { usePaginatedResponse, useData } from '../../model/data';
import { getUsers, fullUserName, UserEdit, User } from '../../model/user';
import { serializeQuery } from '../../model/filter';
import { useMeta } from '../../model/meta';
import { Avatar } from '../../components/Avatar/Avatar';
import { Icon } from '../../components/Icon/Icon';
import { UserForm } from './UserForm/UserForm';
import { history, useQueryAsState } from '../../model/history';
import { userPath } from '../../routes';
import { FilterBar } from '../../components/FilterBar/FilterBar';
import { t } from '../../locales/translate';
import { ControlledModal } from '../../components/Modal/ControlledModal';
import { compact } from 'lodash';

export function Users(): JSX.Element {
  const [addModal, setAddModal] = useState(false);
  const [params] = useQueryAsState();
  const paginator = usePaginatedResponse(
    (page, perPage) => getUsers(page, perPage, serializeQuery(params.query)),
    serializeQuery(params.query)
  );
  const users = useData((db) => compact(paginator.currentIds.map((e) => db.table('users').find(e))));
  useMeta({ title: t.model.user._plural });

  return (
    <Container className="users">
      <Card
        className="users__list"
        title={t.model.user._plural}
        elevation={2}
        pagination={<Pagination {...paginator} />}
        fullbleed
        actions={<Icon onClick={() => setAddModal(true)} name="plus" />}
      >
        <SortableList
          items={users}
          loading={paginator.loading}
          actions={<FilterBar filters={['query']} />}
          size="lg"
          onRowClick={(row: User) => history.push(userPath(row))}
          columns={[
            {
              width: '40px',
              sortable: false,
              dataKey: 'avatar',
              cellRenderer: (row) => {
                return <Avatar image={row.avatar && row.avatar.url} name={row.firstName || ''} size="large" />;
              },
            },
            {
              label: t.model.user.name,
              width: '400px',
              sortable: true,
              dataKey: 'firstName',
              cellRenderer: (row) => fullUserName(row),
            },
            {
              label: t.model.user.email,
              width: '180px',
              sortable: true,
              dataKey: 'email',
            },
            {
              label: t.model.user.phone,
              sortable: false,
              width: '30px',
              dataKey: 'phone',
            },
            {
              label: t.general.locale,
              sortable: false,
              width: '30px',
              dataKey: 'locale',
            },
          ]}
        />
      </Card>
      <ControlledModal<UserEdit>
        open={addModal}
        model={{} as UserEdit}
        onClose={() => setAddModal(false)}
        title={t.actions.add(t.model.user._name)}
        size="small"
      >
        {(controller) => <UserForm controller={controller} />}
      </ControlledModal>
    </Container>
  );
}
